<template>
  <button
    class="toggle"
    :class="[sizeClass, { 'is-on': isOn, disabled }]"
    type="button"
    role="switch"
    :aria-checked="isOn.toString()"
    :aria-disabled="disabled.toString()"
    @click="toggle"
    @keydown.space.prevent="toggle"
    @keydown.enter.prevent="toggle"
  >
    <span class="track"></span>
    <span class="thumb"></span>
    <slot />
  </button>
</template>

<script setup>
import { computed } from 'vue'

const sizeMap = {
  sm: { w: 32, h: 18, thumb: 14 },
  md: { w: 44, h: 24, thumb: 20 },
  lg: { w: 60, h: 34, thumb: 28 },
}

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  size: { type: String, default: 'md', validator: v => ['sm','md','lg'].includes(v) },
  activeColor: { type: String, default: '#10b981' },
  inactiveColor: { type: String, default: '#d1d5db' },
  disabled: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue','change'])

const isOn = computed(() => props.modelValue)
const sizeConf = computed(() => sizeMap[props.size])

const sizeClass = computed(() => `toggle--${props.size}`)

const thumbTranslate = computed(() => {
  const { w, thumb } = sizeConf.value
  // padding = 2px each side
  return isOn.value ? `${w - thumb - 4}px` : '0px'
})

function toggle() {
  if (props.disabled) return
  emit('update:modelValue', !isOn.value)
  emit('change', !isOn.value)
}
</script>

<style scoped>
.toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  padding: 0;
  border: none;
  background: transparent;
  outline: none;
  user-select: none;
}
.toggle.disabled {
  cursor: not-allowed;
  opacity: .6;
}
.toggle:focus-visible .track {
  box-shadow: 0 0 0 3px rgba(59,130,246,.4);
}

.track {
  position: relative;
  display: inline-block;
  border-radius: 999px;
  transition: background-color .22s;
  background-color: v-bind('inactiveColor');
}

.thumb {
  position: absolute;
  top: 50%;
  left: 2px;
  transform: translateY(-50%);
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,.3);
  transition: transform .22s;
  will-change: transform;
}

/* Sizes */
.toggle--sm .track {
  width: 32px;
  height: 18px;
}
.toggle--sm .thumb {
  width: 14px;
  height: 14px;
}

.toggle--md .track {
  width: 44px;
  height: 24px;
}
.toggle--md .thumb {
  width: 20px;
  height: 20px;
}

.toggle--lg .track {
  width: 60px;
  height: 34px;
}
.toggle--lg .thumb {
  width: 28px;
  height: 28px;
}

/* Active state */
.is-on .track {
  background-color: v-bind('activeColor');
}

/* Dynamic thumb translation */
.thumb {
  transform: translateY(-50%) translateX(v-bind('thumbTranslate'));
}
</style>